import {useAiChat} from '@/ai-chat/providers/ai-chat.context';
import {LoadingStates} from '@Utils/loading.util';
import {MOBILE_MODAL_WIDTH_THRESHOLD} from '@Components/modal';
import {closeAiActionLoadingModal, isCurrentModalSession, openMdeLoadingModalAndGetSessionId, showMdeReadyModal} from '@Components/ai-chat-window/lib/ai-action-loading-modal.library';
import {clearPendingInChatJob, isInChatJobHandedOffToGrowl, markInChatJobPending} from '@Components/ai-chat-window/lib/in-chat-job-handoff.library';
import {
  getMdePosterUrl,
  MDE_POLL_BUDGET_MS,
  openMdePendingEditorTab,
  pollMdeJob,
  showMdeDoneGrowl,
  showMdeErrorGrowl,
  showMdeOngoingGrowl,
  showMdeStillWorkingGrowl,
  startMdeJob,
} from '@Libraries/make-design-editable.library';
import type {MdeStartResponse} from '@Libraries/make-design-editable.library.types';

interface UseMakeDesignEditableProps {
  chatId: string;
}

interface AnnounceFinishedDesignOptions {
  navigationUrl: string;
  sessionId: string;
  isHandedOffToGrowl: boolean;
}

interface UseMakeDesignEditable {
  makeDesignEditable: (hashedId: string) => Promise<void>;
}

/** The row's spinner lives in the shared per-image state so every mounted edit menu agrees on which image is running. */
export const useMakeDesignEditable = ({chatId}: UseMakeDesignEditableProps): UseMakeDesignEditable => {
  const {actions} = useAiChat();

  const setLoadingState = (hashedId: string, loadingState: LoadingStates): void => {
    actions.updateLoadingStatesForImageActions(chatId, hashedId, {makeDesignEditableLoadingState: loadingState});
  };

  const failRun = (hashedId: string, error: unknown): void => {
    setLoadingState(hashedId, LoadingStates.NOT_LOADED);
    showMdeErrorGrowl(error);
    console.error(error);
  };

  /**
   * Mobile waits in the chat. So does desktop when no poster was pre-created (the image had no dimensions), since there
   * is no tab to open.
   */
  const makeDesignEditable = async (hashedId: string): Promise<void> => {
    setLoadingState(hashedId, LoadingStates.LOADING);
    let start: MdeStartResponse;
    try {
      start = await startMdeJob(hashedId);
    } catch (e: unknown) {
      failRun(hashedId, e);
      return;
    }

    const {jobId, posterHashedId} = start;
    if (posterHashedId === null || window.innerWidth < MOBILE_MODAL_WIDTH_THRESHOLD) {
      await waitForJobInChat(hashedId, jobId);
      return;
    }
    setLoadingState(hashedId, LoadingStates.LOADED);
    openMdePendingEditorTab(jobId, posterHashedId);
  };

  /** Mobile and the desktop fallback. The ready modal is skipped when another AI action has taken the modal over. */
  const waitForJobInChat = async (hashedId: string, jobId: string): Promise<void> => {
    const pendingJobId = markInChatJobPending({showOngoingGrowl: showMdeOngoingGrowl});
    const sessionId = openMdeLoadingModalAndGetSessionId();

    try {
      const outcome = await pollMdeJob(jobId, MDE_POLL_BUDGET_MS);
      if (outcome === null) {
        settleStillRunningJob(hashedId, pendingJobId);
        return;
      }
      setLoadingState(hashedId, LoadingStates.LOADED);
      const isHandedOffToGrowl = isInChatJobHandedOffToGrowl(pendingJobId);
      clearPendingInChatJob(pendingJobId);
      announceFinishedDesign({navigationUrl: getMdePosterUrl(outcome), sessionId, isHandedOffToGrowl});
    } catch (e: unknown) {
      const isHandedOffToGrowl = isInChatJobHandedOffToGrowl(pendingJobId);
      clearPendingInChatJob(pendingJobId);
      if (!isHandedOffToGrowl) {
        closeAiActionLoadingModal();
      }
      failRun(hashedId, e);
    }
  };

  /** The budget ran out but the job may still finish: not a failure, so the row is re-enabled and the user is told it is still working. */
  const settleStillRunningJob = (hashedId: string, pendingJobId: string): void => {
    const isHandedOffToGrowl = isInChatJobHandedOffToGrowl(pendingJobId);
    clearPendingInChatJob(pendingJobId);
    if (!isHandedOffToGrowl) {
      closeAiActionLoadingModal();
    }
    setLoadingState(hashedId, LoadingStates.NOT_LOADED);
    showMdeStillWorkingGrowl();
  };

  return {makeDesignEditable};
};

const announceFinishedDesign = ({navigationUrl, sessionId, isHandedOffToGrowl}: AnnounceFinishedDesignOptions): void => {
  if (isHandedOffToGrowl) {
    showMdeDoneGrowl(navigationUrl);
  } else if (isCurrentModalSession(sessionId)) {
    showMdeReadyModal(navigationUrl);
  }
};
